import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { 
  Button, TextField, Box, Divider, Typography, CircularProgress, Paper 
} from "@material-ui/core";
import DragNDrop from "../Upload/DragNDrop";
import { connect } from "react-redux";
import { uploadSheet, resetData } from "../../Redux/Actions/dataActions";
import axios from "axios";
import {displayDateByLanguage, formatMsToDuration } from "../../Utils/utils";


function MdlSidebarSheetUpload(props) {
  const { t } = useTranslation();

  const [disabled, setDisabled] = useState(true);
  const [categories, setCategories] = useState([]);
  const userLang = props.userData?.language || 'de';

  // Zentraler Zustand für alle Eingabefelder (inklusive Collection)
  const [requestData, setRequestData] = useState({
    composer: "",
    sheetName: "",
    releaseDate: "1990-01-01", 
    collection: "",            // Album-Titel
    informationText: "",
    uploadFile: undefined,
    keySignature: "",
    tempo: 0,
    duration: "",
    instrument: "",
    youtubeLink: "",
    tags: "",        
    category: "",    
	trackTimeMillis: 0,
	primaryGenreName: "",
	mbId: "", 
	itunesId: "",
	trackViewUrl: "",
	compMbArtistID: "",
	compName: "",
	compType: "",
	compCountry: "",
	compGender: "",
	compDisambiguation: "",
	compBegin: "1990-01-01",
	compEnd: "1990-01-01",
	compIsEndet: false,
	
  });

  const [sheetImageUrl, setSheetImageUrl] = useState("");
  const [composerImageUrl, setComposerImageUrl] = useState("");

  // Bild-Vorschauen Lade- und Fehlerzustände
  const [sheetImgLoading, setSheetImgLoading] = useState(false);
  const [sheetImgError, setSheetImgError] = useState(false);
  const [composerImgLoading, setComposerImgLoading] = useState(false);
  const [composerImgError, setComposerImgError] = useState(false);

  // Zustände für die interaktive Trefferliste
  const [apiLoading, setApiLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]); 

  // Live-Laden der vordefinierten Admin-Kategorien (songtype)
  useEffect(() => {
    const lang = props.userData?.language || "de";
    axios.get(`/settings/options?type=songtype&lang=${lang}`)
      .then(res => { if (res.data) setCategories(res.data); })
      .catch(err => console.error("Fehler beim Laden der Kategorien:", err));
  }, [props.userData]);

  const giveModalData = (file) => {
    setRequestData((prev) => ({ ...prev, uploadFile: file }));
  };

  // Pflichtfeld-Validierung vor Freischaltung des Speichern-Buttons
  useEffect(() => {
    const hasComposer = requestData.composer && requestData.composer.trim() !== "";
    const hasSheetName = requestData.sheetName && requestData.sheetName.trim() !== "";
    const hasFile = requestData.uploadFile !== undefined;
    setDisabled(!(hasComposer && hasSheetName && hasFile));
  }, [requestData.composer, requestData.sheetName, requestData.uploadFile]);

  const handleChange = (event) => {
    const value = event.target.name === "tempo" ? parseInt(event.target.value, 10) || 0 : event.target.value;
    setRequestData({ ...requestData, [event.target.name]: value });
  };

  // Holt die Top 5 Trefferliste vom getunnelten Go-Server
  const handleSheetNameBlur = () => {
    if (!requestData.sheetName.trim()) return;
    
    // Kurze Verzögerung, damit Klicks auf die Ergebnisliste registriert werden können
    setTimeout(() => {
      setApiLoading(true);
      let term = requestData.sheetName;
      if (requestData.composer.trim() !== "") term += ` ${requestData.composer}`;

      axios.get(`/sheet/autofill`, { params: { term: term } })
        .then((res) => {
          if (res.data && res.data.length > 0) {
            setSearchResults(res.data);
          }
        })
        .catch((err) => console.error("Fehler beim Holen der Trefferliste:", err))
        .finally(() => setApiLoading(false));
    }, 200);
  };

  // Befüllt alle Felder des ausgewählten Songs und triggert MusicBrainz im Hintergrund
  // ⭐ SELECT-ACTION: Mappt die Werte des gewählten Songs (Jetzt NEU: Inklusive hochskaliertem Cover-Bild!)
  const handleSelectSong = (song) => {
    setSearchResults([]); 
    setApiLoading(true);

    const formattedDuration = formatMsToDuration(song.durationMs);

    // 1. Wir holen uns den originalen Bild-Link von Apple (falls vorhanden)
    // iTunes liefert meistens Felder wie artworkUrl60 oder artworkUrl100
    let highResCoverUrl = "";
    const rawArtworkUrl = song.artworkUrl100 || song.artworkUrl60 || "";

    if (rawArtworkUrl !== "") {
      console.log("🖼️ Originales Apple-Cover-Bild (Klein):", rawArtworkUrl);
      
      // ⭐ DIE UNZERSTÖRBARE REGEX-RETTUNG:
      // Sucht dynamisch nach Mustern wie /100x100 oder /60x60 und ersetzt es sauber durch /400x400.
      // Das fängt alle Apple-Dateiendungen und Suffixe fehlerfrei ab!
      highResCoverUrl = rawArtworkUrl.replace(/\/\d+x\d+/, "/400x400");
        
      console.log("🚀 Hochskaliertes Premium-Cover-Bild (400x400):", highResCoverUrl);
    }
	
    // 2. Vorab-Injektion der Apple-Werte in das Formular
    setRequestData((prev) => ({
      ...prev,
      sheetName: song.title,
      composer: song.artist,
      collection: song.collection, 
      releaseDate: song.releaseDate,
      duration: formattedDuration,
	  sheetImageUrl: highResCoverUrl,
	  itunesId: song.trackId,
	  trackViewUrl: song.trackViewUrl,
	  category: song.primaryGenreName
    }));

	console.log("Inhalt von song aus dem Apple:", song);
	
    // ⭐ NEU: Wir belegen das Vorschau-Bild-Feld direkt mit der scharfen URL vor!
    if (highResCoverUrl !== "") {
      setSheetImageUrl(highResCoverUrl);
      
      // Wir triggern im selben Moment im Hintergrund eine diskrete Bild-Validierung,
      // damit der User sofort sieht, dass das Vorschaubild geladen wurde.
      const img = new Image();
      img.src = highResCoverUrl;
      img.onload = () => {
        setSheetImgLoading(false);
        setSheetImgError(false);
      };
      img.onerror = () => {
        setSheetImgLoading(false);
        setSheetImgError(true);
      };
    }

	// ==========================================
	// LOGIK FÜR DIE ERSTE ABFRAGE (/sheet/details)
	// ==========================================
	const fetchSheetDetailsWithRetry = (sheetRetriesLeft) => {
	  axios.get(`/sheet/details`, { params: { title: song.title, artist: song.artist } })
		.then((res) => {
		  if (res.data) {
			// Ersten Datensatz im State sichern
			setRequestData((prev) => ({
			  ...prev,
			  keySignature: res.data.key || prev.keySignature,
			  tempo: res.data.bpm > 0 ? res.data.bpm : prev.tempo,
			  mbId: res.data.mbId || prev.mbId,
			  artistId: res.data.artistId || prev.artistId,
			}));
			
			console.log(`🟢 MusicBrainz-Daten erfolgreich im Formular hinterlegt. - mbId: ${res.data.mbId} - artistId: ${res.data.artistId}`);

			// ==========================================
			// LOGIK FÜR DIE ZWEITE ABFRAGE (/composer/details)
			// ==========================================
			// 🛑 KORREKTUR: Direkt prüfen und feuern, wenn die artistId in der Antwort existiert!
			if (res.data.artistId && res.data.artistId !== "") {
			  console.log(`📡 Starte Folge-Abfrage für Artist-Details mit ID: ${res.data.artistId}`);
			  
			  const fetchComposerWithRetry = (composerRetriesLeft) => {
				axios.get(`/composer/details`, { params: { artistId: res.data.artistId } })
				  .then((composerRes) => {
					if (composerRes.data) {
					  setComposerImageUrl(composerRes.data.ImageUrl);
					  setRequestData((prev) => ({
						...prev,
						compMbArtistID: res.data.artistId,
						compName: composerRes.data.name || prev.compName,
						compDisambiguation: composerRes.data.disambiguation || prev.compDisambiguation,
						compType: composerRes.data.type || prev.compType,
						compGender: composerRes.data.gender || prev.compGender,
						compBegin: composerRes.data.begin || prev.compBegin,
						compCountry: composerRes.data.country || prev.compCountry,
						compEnd: composerRes.data.end || prev.compEnd,
						compIsEndet: composerRes.data.isendet || prev.compIsEndet,
						compArea: composerRes.data.area || prev.compArea,
						compImageUrl: composerRes.data.ImageUrl|| prev.ImageUrl,
						compIMDbUrl: composerRes.data.IMDbUrl || prev.IMDbUrl,
						composerImageUrl: composerRes.data.ImageUrl || prev.ImageUrl,
					  }));
					}
					// Erfolg: Loading beenden
					setApiLoading(false);
				  })
				  .catch((composerErr) => {
					if (composerRetriesLeft > 0) {
					  console.warn(`Fehler beim Artist-Autofill. Starte Versuch erneut. Verbleibende Versuche: ${composerRetriesLeft}. Fehler:`, composerErr);
					  
					  setTimeout(() => {
						fetchComposerWithRetry(composerRetriesLeft - 1);
					  }, 1000);
					} else {
					  console.error("Fehler beim Artist-Autofill nach 3 Wiederholungen:", composerErr);
					  setApiLoading(false);
					}
				  });
			  };

			  // Starte die zweite Abfrage (max 3 Wiederholungen)
			  fetchComposerWithRetry(3);

			} else {
			  // Falls keine artistId geliefert wurde, das Loading hier beenden
			  setApiLoading(false);
			}

		  } else {
			// Keine Daten in der Antwort der ersten Abfrage erhalten
			setApiLoading(false);
		  }
		})
		.catch((sheetErr) => {
		  if (sheetRetriesLeft > 0) {
			console.warn(`Fehler beim Detail-Matching. Starte Versuch erneut. Verbleibende Versuche: ${sheetRetriesLeft}. Fehler:`, sheetErr);
			
			// 1 Sekunde warten vor dem nächsten Versuch
			setTimeout(() => {
			  fetchSheetDetailsWithRetry(sheetRetriesLeft - 1);
			}, 1000);
		  } else {
			// Alle Versuche für die erste Abfrage fehlgeschlagen
			console.error("Fehler beim Detail-Matching nach 3 Wiederholungen:", sheetErr);
			setApiLoading(false);
		  }
		});
	};

	// Initialer Start der gesamten Kette (erste Abfrage mit max 3 Wiederholungen)
	fetchSheetDetailsWithRetry(3);
	
  };
  
  // Bildvorschauen-Validierung beim Verlassen der Felder
  const handleSheetUrlBlur = () => {
    if (!sheetImageUrl.trim()) { setSheetImgError(false); return; }
    setSheetImgLoading(true); setSheetImgError(false);
    const img = new Image(); img.src = sheetImageUrl;
    img.onload = () => setSheetImgLoading(false);
    img.onerror = () => { setSheetImgLoading(false); setSheetImgError(true); };
  };

  const handleComposerUrlBlur = () => {
    if (!composerImageUrl.trim()) { setComposerImgError(false); return; }
    setComposerImgLoading(true); setComposerImgError(false);
    const img = new Image(); img.src = composerImageUrl;
    img.onload = () => setComposerImgLoading(false);
    img.onerror = () => { setComposerImgLoading(false); setComposerImgError(true); };
  };

  const sendRequest = () => {
    const newData = {
      uploadFile: requestData.uploadFile,
      sheetName: requestData.sheetName,
      composer: requestData.composer,
      releaseDate: requestData.releaseDate,
      collection: requestData.collection, 
      informationText: requestData.informationText,
      sheetImageUrl: sheetImageUrl,
      composerImageUrl: composerImageUrl,
      keySignature: requestData.keySignature,
      tempo: requestData.tempo,
      duration: requestData.duration,
      instrument: requestData.instrument,
      youtubeLink: requestData.youtubeLink,
      tags: requestData.tags,         
      category: requestData.category, 
	  mbId: requestData.mbId, 
	  itunesId: requestData.itunesId,
	  trackViewUrl: requestData.trackViewUrl,
	  compMbArtistID: requestData.artistId,
	  compDisambiguation: requestData.compDisambiguation,
	  compType: requestData.compType,
	  compGender: requestData.compGender,
	  compBegin: requestData.compBegin,
	  compEnd: requestData.compEnd,
	  compIsEndet: requestData.compIsEndet,
	  compArea: requestData.compArea,
	  compCountry: requestData.compCountry,
	  compIMDbUrl: requestData.compIMDbUrl
    };

	console.log("Inhalt von newData aus dem sendRequest:", newData);

    props.uploadSheet(newData, () => {
      props.onClose();
      props.resetData();
      setTimeout(() => { window.location.reload(); }, 350);
    });
  };

  return (
    <Box 
      style={{ 
        width: "100%", 
        maxWidth: "460px", 
        margin: "0 auto", 
        padding: "10px 5px", 
        maxHeight: "70vh", 
        overflowY: "auto" 
      }}
    >
      <form noValidate autoComplete="off" style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        
        {/* Sektion 1: Stammdaten */}
        <Box style={{ display: "flex", flexDirection: "column", gap: "16px", position: "relative" }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" style={{ color: "#4f40b3", fontWeight: "bold", letterSpacing: "1px", textTransform: "uppercase" }}>
				{t("MdlSidebarSheetUpload.subtitle_section_1")}
            </Typography>
            {apiLoading && (
              <Box display="flex" alignItems="center" style={{ gap: "6px" }}>
                <CircularProgress size={14} style={{ color: "#4f40b3" }} />
                <Typography variant="caption" style={{ color: "#757575", fontSize: "11px" }}>{t("common.search_itunes")}...</Typography>
              </Box>
            )}
          </Box>

          <TextField 
            fullWidth 
            variant="outlined" 
            size="small" 
            label={t("common.song_title")} 
            name="sheetName" 
            value={requestData.sheetName} 
            onChange={handleChange}
            onBlur={handleSheetNameBlur} 
          />

          {/* INTERAKTIVE INTERPRETEN- & SONG-AUSWAHLLISTE */}
          {searchResults.length > 0 && (
            <Paper 
              elevation={3} 
              style={{ 
                position: "absolute", top: "75px", left: 0, right: 0, zIndex: 9999, 
                backgroundColor: "white", maxHeight: "200px", overflowY: "auto", 
                borderRadius: "6px", border: "1px solid #e0e0e0" 
              }}
            >
              {searchResults.map((song) => (
                <Box 
                  key={song.track_id}
                  onClick={() => handleSelectSong(song)}
                  style={{ 
                    padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid #f0f0f0", 
                    textAlign: "left", transition: "background 0.2s" 
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f5f4fa"}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                >
                  <Typography variant="body2" style={{ fontWeight: "bold", color: "#1d1b31" }}>
                    {song.title}
                  </Typography>
                  <Typography variant="caption" style={{ color: "#757575" }}>
                    {t("common.from")} {song.artist} • {t("sheet.collection")}: {song.collection || "Unbekannt"} ({song.release_date ? displayDateByLanguage(song.release_date, userLang) : "-"})
                  </Typography>
                </Box>
              ))}
            </Paper>
          )}

          <TextField 
            fullWidth 
            variant="outlined" 
            size="small" 
            label={t("common.composer")}
            name="composer" 
            value={requestData.composer} 
            onChange={handleChange} 
          />

          <TextField 
            fullWidth 
            variant="outlined" 
            size="small" 
            label={t("sheet.collection")} 
            name="collection" 
            value={requestData.collection} 
            onChange={handleChange} 
          />
          
          {/* Natives Kategorie-Dropdown */}
          <Box display="flex" flexDirection="column" style={{ gap: "4px" }}>
            <Typography variant="caption" style={{ color: "#757575", fontWeight: "600", marginLeft: "4px" }}>
              {t("sheet.category")}
            </Typography>
            <select
              name="category"
              value={requestData.category}
              onChange={handleChange}
              style={{
                width: "100%", padding: "10px 14px", borderRadius: "4px", borderColor: "#c4c4c4",
                backgroundColor: "white", fontSize: "15px", fontFamily: "inherit", color: "#1d1b31",
                cursor: "pointer", outline: "none"
              }}
            >
              <option value="">{t("common.no_values")}</option>
              {categories && categories.map((cat) => (
                <option key={cat.id || cat.value} value={cat.value}>{cat.value}</option>
              ))}
            </select>
          </Box>
        </Box>

        <Divider style={{ margin: "5px 0" }} />

        {/* Sektion 2: Musikalische Details */}
        <Box style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <Typography variant="caption" style={{ color: "#4f40b3", fontWeight: "bold", letterSpacing: "1px", textTransform: "uppercase" }}>
            {t("MdlSidebarSheetUpload.subtitle_section_2")}
          </Typography>
          <TextField fullWidth variant="outlined" size="small" label={t("sheet.keySignature")} name="keySignature" value={requestData.keySignature} onChange={handleChange} />
          <TextField fullWidth variant="outlined" size="small" type="number" label={t("sheet.tempo")} name="tempo" value={requestData.tempo === 0 ? "" : requestData.tempo} onChange={handleChange} />
          <TextField fullWidth variant="outlined" size="small" label={t("sheet.duration")} name="duration" value={requestData.duration} onChange={handleChange} />
          <TextField fullWidth variant="outlined" size="small" label={t("sheet.instrument")} name="instrument" value={requestData.instrument} onChange={handleChange} />
          <TextField fullWidth variant="outlined" size="small" label={t("sheet.youtubeLink")} name="youtubeLink" value={requestData.youtubeLink} onChange={handleChange} />
		  <TextField fullWidth variant="outlined" size="small" label={t("sheet.tags")} name="tags" value={requestData.tags} onChange={handleChange} placeholder={t("sheet.placeholder_tags")} />
        </Box>

        <Divider style={{ margin: "5px 0" }} />

        {/* Sektion 3: Beschreibungen & URLs */}
        <Box style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <Typography variant="caption" style={{ color: "#4f40b3", fontWeight: "bold", letterSpacing: "1px", textTransform: "uppercase" }}>
            {t("MdlSidebarSheetUpload.subtitle_section_3")}
          </Typography>
          <TextField fullWidth variant="outlined" size="small" label={t("sheet.informationText")} name="informationText" value={requestData.informationText} onChange={handleChange} multiline minRows={3} />
          
          {/* 1. NOTENBLATT-PREVIEW-URL */}
          <Box display="flex" flexDirection="column" style={{ gap: "8px" }}>
            <TextField 
              fullWidth variant="outlined" size="small" label={`${t("common.sheet")} ${t("files.preview")} ${t("files.upload_url")} (${t("common.optional")})`}
              name="sheetImageUrl" value={sheetImageUrl} 
			  onChange={(e) => {
                setSheetImageUrl(e.target.value);
                setRequestData(prev => ({ ...prev, sheetImageUrl: e.target.value }));
              }}  
              onBlur={handleSheetUrlBlur} error={sheetImgError}
            />
            {sheetImageUrl.trim() !== "" && (
              <Paper 
                elevation={0} 
                style={{ 
                  height: "100px", display: "flex", justifyContent: "center", alignItems: "center", 
                  backgroundColor: "#fafafa", borderRadius: "8px", overflow: "hidden", 
                  border: sheetImgError ? "1px solid #f44336" : "1px dashed #ccc" 
                }}
              >
                {sheetImgLoading ? (
                  <Box display="flex" alignItems="center" style={{ gap: "10px" }}>
                    <CircularProgress size={18} style={{ color: "#4f40b3" }} />
                    <Typography variant="caption" style={{ color: "#757575" }}>{t("files.check_preview")}...</Typography>
                  </Box>
                ) : sheetImgError ? (
                  <Typography variant="caption" style={{ color: "#f44336", fontWeight: "bold" }}>{t("files.no_image")}...❌</Typography>
                ) : (
                  <img src={sheetImageUrl} alt={`${t("common.sheet")} ${t("files.thumbnail")}`} style={{ height: "100%", width: "100%", objectFit: "contain" }} />
                )}
              </Paper>
            )}
          </Box>

          {/* 2. COMPOSER-PORTRAIT-URL */}
          <Box display="flex" flexDirection="column" style={{ gap: "8px" }}>
            <TextField 
              fullWidth variant="outlined" size="small" label={`${t("common.composer")} ${t("files.thumbnail")} ${t("files.upload_url")} (${t("common.optional")})`} 
              name="composerImageUrl" value={composerImageUrl} onChange={(e) => setComposerImageUrl(e.target.value)} 
              onBlur={handleComposerUrlBlur} error={composerImgError}
            />
            {composerImageUrl.trim() !== "" && (
              <Paper 
                elevation={0} 
                style={{ 
                  height: "100px", display: "flex", justifyContent: "center", alignItems: "center", 
                  backgroundColor: "#fafafa", borderRadius: "8px", overflow: "hidden", 
                  border: composerImgError ? "1px solid #f44336" : "1px dashed #ccc" 
                }}
              >
                {composerImgLoading ? (
                  <Box display="flex" alignItems="center" style={{ gap: "10px" }}>
                    <CircularProgress size={18} style={{ color: "#4f40b3" }} />
                    <Typography variant="caption" style={{ color: "#757575" }}>{t("files.check_preview")}...</Typography>
                  </Box>
                ) : composerImgError ? (
                  <Typography variant="caption" style={{ color: "#f44336", fontWeight: "bold" }}>{t("files.no_image")}...❌</Typography>
                ) : (
                  <img src={composerImageUrl} alt={`${t("common.composer")} ${t("files.thumbnail")}`} style={{ height: "100%", width: "100%", objectFit: "contain" }} />
                )}
              </Paper>
            )}
          </Box>
        </Box>

        {/* Datei Drag & Drop */}
        <Box style={{ marginTop: "10px", marginBottom: "10px" }}>
          <DragNDrop giveModalData={giveModalData} />
        </Box>

        {/* Aktions-Buttons */}
        <Box style={{ display: "flex", justifyContent: "space-between", gap: "12px", marginTop: "10px" }}>
          <Button variant="outlined" onClick={props.onClose} style={{ borderRadius: "8px", padding: "10px 20px", textTransform: "none" }}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            disabled={disabled}
            onClick={sendRequest}
            style={{ 
              borderRadius: "8px", padding: "10px 20px", textTransform: "none", 
              backgroundColor: disabled ? "#e0e0e0" : "#4f40b3", 
              color: disabled ? "#a6a6a6" : "white" 
            }}
          >
            {t("common.save")}
          </Button>
        </Box>
      </form>
    </Box>
  );
}

const mapStateToProps = (state) => ({
  userData: state.user.userData,
});

const mapActionsToProps = {
  uploadSheet,
  resetData,
};

export default connect(mapStateToProps, mapActionsToProps)(MdlSidebarSheetUpload);